import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, MessageSquare, Building2, Tv, Package, 
  LogOut, RefreshCw, Trash2, CheckCircle, Clock, XCircle, 
  ChevronRight, Plus, Eye, Mail, Phone, MapPin, Play, Smartphone, Edit, X, Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [ispInquiries, setIspInquiries] = useState([]);
  const [broadcasters, setBroadcasters] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  
  // Modal states
  const [showBroadcasterModal, setShowBroadcasterModal] = useState(false);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [editingBroadcaster, setEditingBroadcaster] = useState(null);
  const [selectedBroadcaster, setSelectedBroadcaster] = useState(null);
  const [newBroadcaster, setNewBroadcaster] = useState({ name: '', description: '', logo_color: '#D92323' });
  const [newChannel, setNewChannel] = useState({ name: '', category: 'Entertainment', language: 'Hindi', quality: 'SD', price: 0, genre: 'General', is_fta: false });

  const getAuthHeader = useCallback(() => {
    const stored = localStorage.getItem('adminAuth');
    if (stored) {
      const { username, password } = JSON.parse(stored);
      return { Authorization: 'Basic ' + btoa(`${username}:${password}`) };
    }
    return {};
  }, []);

  const verifyAuth = useCallback(async () => {
    const stored = localStorage.getItem('adminAuth');
    if (!stored) { setIsLoading(false); return; }
    try {
      const { username, password } = JSON.parse(stored);
      await axios.get(`${API}/admin/verify`, { headers: { Authorization: 'Basic ' + btoa(`${username}:${password}`) } });
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('adminAuth');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { verifyAuth(); }, [verifyAuth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.get(`${API}/admin/verify`, { headers: { Authorization: 'Basic ' + btoa(`${credentials.username}:${credentials.password}`) } });
      localStorage.setItem('adminAuth', JSON.stringify(credentials));
      setIsAuthenticated(true);
      toast.success('Login successful');
    } catch (error) {
      toast.error('Invalid credentials');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
  };

  // Fetch functions
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard-stats`, { headers: getAuthHeader() });
      setDashboard(response.data);
    } catch (error) { console.error('Error:', error); }
  }, [getAuthHeader]);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`, { headers: getAuthHeader() });
      setContacts(response.data);
    } catch (error) { console.error('Error:', error); }
  }, [getAuthHeader]);

  const fetchIspInquiries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/isp-inquiries`, { headers: getAuthHeader() });
      setIspInquiries(response.data);
    } catch (error) { console.error('Error:', error); }
  }, [getAuthHeader]);

  const fetchBroadcasters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/broadcasters`, { headers: getAuthHeader() });
      setBroadcasters(response.data);
    } catch (error) { console.error('Error:', error); }
  }, [getAuthHeader]);

  const fetchDemoRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/demo-requests`, { headers: getAuthHeader() });
      setDemoRequests(response.data);
    } catch (error) { console.error('Error:', error); }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboard(); fetchContacts(); fetchIspInquiries(); fetchBroadcasters(); fetchDemoRequests();
    }
  }, [isAuthenticated, fetchDashboard, fetchContacts, fetchIspInquiries, fetchBroadcasters, fetchDemoRequests]);

  // CRUD Operations
  const updateContactStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success('Status updated'); fetchContacts(); fetchDashboard();
    } catch (error) { toast.error('Failed to update'); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted'); fetchContacts(); fetchDashboard();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const updateIspStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/isp-inquiries/${id}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success('Status updated'); fetchIspInquiries(); fetchDashboard();
    } catch (error) { toast.error('Failed to update'); }
  };

  const deleteIspInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await axios.delete(`${API}/admin/isp-inquiries/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted'); fetchIspInquiries(); fetchDashboard();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const updateDemoStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/demo-requests/${id}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success('Status updated'); fetchDemoRequests(); fetchDashboard();
    } catch (error) { toast.error('Failed to update'); }
  };

  const deleteDemoRequest = async (id) => {
    if (!window.confirm('Delete this demo request?')) return;
    try {
      await axios.delete(`${API}/admin/demo-requests/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted'); fetchDemoRequests(); fetchDashboard();
    } catch (error) { toast.error('Failed to delete'); }
  };

  // Broadcaster CRUD
  const createBroadcaster = async () => {
    if (!newBroadcaster.name) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API}/admin/broadcasters`, newBroadcaster, { headers: getAuthHeader() });
      toast.success('Broadcaster created'); setShowBroadcasterModal(false);
      setNewBroadcaster({ name: '', description: '', logo_color: '#D92323' }); fetchBroadcasters(); fetchDashboard();
    } catch (error) { toast.error('Failed to create'); }
  };

  const updateBroadcaster = async () => {
    if (!editingBroadcaster) return;
    try {
      await axios.put(`${API}/admin/broadcasters/${editingBroadcaster.id}`, {
        name: editingBroadcaster.name, description: editingBroadcaster.description, logo_color: editingBroadcaster.logo_color
      }, { headers: getAuthHeader() });
      toast.success('Updated'); setEditingBroadcaster(null); fetchBroadcasters();
    } catch (error) { toast.error('Failed to update'); }
  };

  const deleteBroadcaster = async (id) => {
    if (!window.confirm('Delete this broadcaster and all its channels?')) return;
    try {
      await axios.delete(`${API}/admin/broadcasters/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted'); fetchBroadcasters(); fetchDashboard();
    } catch (error) { toast.error('Failed to delete'); }
  };

  // Channel CRUD
  const addChannel = async () => {
    if (!selectedBroadcaster || !newChannel.name) { toast.error('Channel name required'); return; }
    try {
      await axios.post(`${API}/admin/broadcasters/${selectedBroadcaster.id}/channels`, newChannel, { headers: getAuthHeader() });
      toast.success('Channel added'); setShowChannelModal(false);
      setNewChannel({ name: '', category: 'Entertainment', language: 'Hindi', quality: 'SD', price: 0, genre: 'General', is_fta: false });
      fetchBroadcasters();
    } catch (error) { toast.error('Failed to add channel'); }
  };

  const deleteChannel = async (broadcasterId, channelName) => {
    if (!window.confirm(`Delete channel "${channelName}"?`)) return;
    try {
      await axios.delete(`${API}/admin/broadcasters/${broadcasterId}/channels/${encodeURIComponent(channelName)}`, { headers: getAuthHeader() });
      toast.success('Channel deleted'); fetchBroadcasters();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const getStatusBadge = (status) => {
    const styles = { new: 'bg-blue-100 text-blue-700', read: 'bg-gray-100 text-gray-700', replied: 'bg-green-100 text-green-700', closed: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-1 rounded-full text-xs font-bold ${styles[status] || styles.new}`}>{(status || 'new').toUpperCase()}</span>;
  };

  const getDemoStatusBadge = (status) => {
    const styles = { active: 'bg-green-100 text-green-700', pending_activation: 'bg-yellow-100 text-yellow-700', expired: 'bg-gray-100 text-gray-700', cancelled: 'bg-red-100 text-red-700' };
    const labels = { active: 'ACTIVE', pending_activation: 'PENDING', expired: 'EXPIRED', cancelled: 'CANCELLED' };
    return <span className={`px-2 py-1 rounded-full text-xs font-bold ${styles[status] || styles.pending_activation}`}>{labels[status] || 'PENDING'}</span>;
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D92323]"></div></div>;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#D92323] rounded-2xl flex items-center justify-center mx-auto mb-4"><Tv size={32} className="text-white" /></div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
            <p className="text-gray-500">A Digital TV Management</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input type="text" value={credentials.username} onChange={(e) => setCredentials({ ...credentials, username: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]" data-testid="admin-username" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input type="password" value={credentials.password} onChange={(e) => setCredentials({ ...credentials, password: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]" data-testid="admin-password" required />
            </div>
            <button type="submit" className="w-full bg-[#D92323] text-white py-3 rounded-xl font-bold hover:bg-red-700 transition-colors" data-testid="admin-login-btn">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D92323] rounded-xl flex items-center justify-center"><Tv size={20} className="text-white" /></div>
            <div><h1 className="font-bold text-gray-900">A Digital Admin</h1><p className="text-xs text-gray-500">Management Portal</p></div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => { fetchDashboard(); fetchContacts(); fetchIspInquiries(); fetchBroadcasters(); fetchDemoRequests(); }} className="p-2 text-gray-500 hover:text-gray-700"><RefreshCw size={20} /></button>
            <button onClick={handleLogout} className="flex items-center gap-2 text-gray-500 hover:text-red-600"><LogOut size={20} /><span className="hidden md:inline">Logout</span></button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="dashboard" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-2 mb-8 bg-white p-2 rounded-xl">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#D92323] data-[state=active]:text-white"><LayoutDashboard size={16} className="mr-2" />Dashboard</TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-[#D92323] data-[state=active]:text-white"><MessageSquare size={16} className="mr-2" />Contacts</TabsTrigger>
            <TabsTrigger value="isp" className="data-[state=active]:bg-[#D92323] data-[state=active]:text-white"><Building2 size={16} className="mr-2" />ISP Inquiries</TabsTrigger>
            <TabsTrigger value="demos" className="data-[state=active]:bg-[#D92323] data-[state=active]:text-white"><Play size={16} className="mr-2" />Demo Requests</TabsTrigger>
            <TabsTrigger value="packages" className="data-[state=active]:bg-[#D92323] data-[state=active]:text-white"><Package size={16} className="mr-2" />Packages</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {dashboard && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center"><Tv size={24} className="text-[#D92323]" /></div><span className="text-3xl font-bold text-gray-900">{dashboard.total_channels || 0}</span></div>
                  <h3 className="font-semibold text-gray-700">Total Channels</h3><p className="text-sm text-gray-500">{dashboard.total_broadcasters || 0} Broadcasters</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><Package size={24} className="text-blue-600" /></div><span className="text-3xl font-bold text-gray-900">{dashboard.total_bouquets || 0}</span></div>
                  <h3 className="font-semibold text-gray-700">Total Bouquets</h3>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><MessageSquare size={24} className="text-green-600" /></div><span className="text-3xl font-bold text-gray-900">{dashboard.contact_messages?.total || 0}</span></div>
                  <h3 className="font-semibold text-gray-700">Contacts</h3><p className="text-sm text-gray-500">{dashboard.contact_messages?.new || 0} New</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center"><Building2 size={24} className="text-orange-600" /></div><span className="text-3xl font-bold text-gray-900">{dashboard.isp_inquiries?.total || 0}</span></div>
                  <h3 className="font-semibold text-gray-700">ISP Inquiries</h3><p className="text-sm text-gray-500">{dashboard.isp_inquiries?.new || 0} New</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4"><div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center"><Play size={24} className="text-purple-600" /></div><span className="text-3xl font-bold text-gray-900">{dashboard.demo_requests?.total || 0}</span></div>
                  <h3 className="font-semibold text-gray-700">Demo Requests</h3><p className="text-sm text-gray-500">{dashboard.demo_requests?.pending || 0} Pending</p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100"><h2 className="text-xl font-bold text-gray-900">Contact Messages</h2></div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50"><tr><th className="text-left px-6 py-4 font-semibold text-gray-700">Name</th><th className="text-left px-6 py-4 font-semibold text-gray-700">Email</th><th className="text-left px-6 py-4 font-semibold text-gray-700">Subject</th><th className="text-left px-6 py-4 font-semibold text-gray-700">Status</th><th className="text-right px-6 py-4 font-semibold text-gray-700">Actions</th></tr></thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr key={contact.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-6 py-4"><p className="font-semibold text-gray-900">{contact.name}</p><p className="text-sm text-gray-500">{new Date(contact.timestamp).toLocaleDateString()}</p></td>
                        <td className="px-6 py-4 text-gray-600">{contact.email}</td>
                        <td className="px-6 py-4 text-gray-600 max-w-xs truncate">{contact.subject}</td>
                        <td className="px-6 py-4">{getStatusBadge(contact.status)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <select value={contact.status || 'new'} onChange={(e) => updateContactStatus(contact.id, e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1">
                              <option value="new">New</option><option value="read">Read</option><option value="replied">Replied</option><option value="closed">Closed</option>
                            </select>
                            <button onClick={() => deleteContact(contact.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {contacts.length === 0 && <div className="text-center py-12 text-gray-500">No contacts yet</div>}
              </div>
            </div>
          </TabsContent>

          {/* ISP Inquiries Tab */}
          <TabsContent value="isp">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100"><h2 className="text-xl font-bold text-gray-900">ISP Inquiries</h2></div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50"><tr><th className="text-left px-6 py-4 font-semibold text-gray-700">Company</th><th className="text-left px-6 py-4 font-semibold text-gray-700">Contact</th><th className="text-left px-6 py-4 font-semibold text-gray-700">Location</th><th className="text-left px-6 py-4 font-semibold text-gray-700">Status</th><th className="text-right px-6 py-4 font-semibold text-gray-700">Actions</th></tr></thead>
                  <tbody>
                    {ispInquiries.map((inquiry) => (
                      <tr key={inquiry.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-6 py-4"><p className="font-semibold text-gray-900">{inquiry.company_name}</p><p className="text-sm text-gray-500">{new Date(inquiry.timestamp).toLocaleDateString()}</p></td>
                        <td className="px-6 py-4"><p className="text-gray-900">{inquiry.contact_person}</p><p className="text-sm text-gray-500">{inquiry.email}</p></td>
                        <td className="px-6 py-4 text-gray-600">{inquiry.city}, {inquiry.state}</td>
                        <td className="px-6 py-4">{getStatusBadge(inquiry.status)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <select value={inquiry.status || 'new'} onChange={(e) => updateIspStatus(inquiry.id, e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1">
                              <option value="new">New</option><option value="read">Read</option><option value="replied">Replied</option><option value="closed">Closed</option>
                            </select>
                            <button onClick={() => deleteIspInquiry(inquiry.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ispInquiries.length === 0 && <div className="text-center py-12 text-gray-500">No inquiries yet</div>}
              </div>
            </div>
          </TabsContent>

          {/* Demo Requests Tab */}
          <TabsContent value="demos">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100"><h2 className="text-xl font-bold text-gray-900">Demo Requests</h2></div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50"><tr><th className="text-left px-6 py-4 font-semibold text-gray-700">Mobile</th><th className="text-left px-6 py-4 font-semibold text-gray-700">Date</th><th className="text-left px-6 py-4 font-semibold text-gray-700">API Status</th><th className="text-left px-6 py-4 font-semibold text-gray-700">Status</th><th className="text-right px-6 py-4 font-semibold text-gray-700">Actions</th></tr></thead>
                  <tbody>
                    {demoRequests.map((demo) => (
                      <tr key={demo.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-6 py-4"><p className="font-semibold text-gray-900 flex items-center gap-2"><Smartphone size={16} />{demo.mobile_no}</p></td>
                        <td className="px-6 py-4 text-sm text-gray-600">{new Date(demo.created_at).toLocaleString()}</td>
                        <td className="px-6 py-4">{demo.api_synced ? <span className="text-green-600 flex items-center gap-1"><CheckCircle size={14} /> Synced</span> : <span className="text-yellow-600 flex items-center gap-1"><Clock size={14} /> Pending</span>}</td>
                        <td className="px-6 py-4">{getDemoStatusBadge(demo.status)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <select value={demo.status} onChange={(e) => updateDemoStatus(demo.id, e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1">
                              <option value="pending_activation">Pending</option><option value="active">Active</option><option value="expired">Expired</option><option value="cancelled">Cancelled</option>
                            </select>
                            <button onClick={() => deleteDemoRequest(demo.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {demoRequests.length === 0 && <div className="text-center py-12 text-gray-500">No demo requests yet</div>}
              </div>
            </div>
          </TabsContent>

          {/* Packages Management Tab */}
          <TabsContent value="packages">
            <div className="space-y-6">
              {/* Broadcasters Section */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                  <div><h2 className="text-xl font-bold text-gray-900">Broadcasters & Packages</h2><p className="text-sm text-gray-500">Manage broadcasters, channels, and bouquets</p></div>
                  <button onClick={() => setShowBroadcasterModal(true)} className="btn-primary flex items-center gap-2"><Plus size={18} /> Add Broadcaster</button>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {broadcasters.map((broadcaster) => (
                      <div key={broadcaster.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: broadcaster.logo_color || '#D92323' }}>
                              {broadcaster.name?.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900">{broadcaster.name}</h3>
                              <p className="text-sm text-gray-500">{broadcaster.channels?.length || 0} channels</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => setEditingBroadcaster(broadcaster)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"><Edit size={16} /></button>
                            <button onClick={() => deleteBroadcaster(broadcaster.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{broadcaster.description}</p>
                        <div className="flex gap-2">
                          <button onClick={() => { setSelectedBroadcaster(broadcaster); setShowChannelModal(true); }} className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full hover:bg-gray-200 flex items-center gap-1">
                            <Plus size={12} /> Add Channel
                          </button>
                          <button onClick={() => setSelectedBroadcaster(selectedBroadcaster?.id === broadcaster.id ? null : broadcaster)} className="text-xs bg-[#D92323]/10 text-[#D92323] px-3 py-1 rounded-full hover:bg-[#D92323]/20 flex items-center gap-1">
                            <Eye size={12} /> View Channels
                          </button>
                        </div>
                        
                        {/* Channel List */}
                        {selectedBroadcaster?.id === broadcaster.id && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <h4 className="font-semibold text-gray-700 mb-2">Channels ({broadcaster.channels?.length || 0})</h4>
                            <div className="max-h-48 overflow-y-auto space-y-1">
                              {broadcaster.channels?.map((channel, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                                  <div>
                                    <span className="font-medium text-gray-900">{channel.name}</span>
                                    <span className="text-gray-500 ml-2">({channel.quality})</span>
                                    {channel.is_fta && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1 rounded">FTA</span>}
                                  </div>
                                  <button onClick={() => deleteChannel(broadcaster.id, channel.name)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                                </div>
                              ))}
                              {(!broadcaster.channels || broadcaster.channels.length === 0) && <p className="text-gray-400 text-sm">No channels</p>}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {broadcasters.length === 0 && <div className="text-center py-12 text-gray-500">No broadcasters yet. Add one to get started.</div>}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Broadcaster Modal */}
      {showBroadcasterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add Broadcaster</h3>
              <button onClick={() => setShowBroadcasterModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label><input type="text" value={newBroadcaster.name} onChange={(e) => setNewBroadcaster({ ...newBroadcaster, name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg" placeholder="e.g., Star India" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><textarea value={newBroadcaster.description} onChange={(e) => setNewBroadcaster({ ...newBroadcaster, description: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg" rows={3} placeholder="Brief description..." /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Brand Color</label><input type="color" value={newBroadcaster.logo_color} onChange={(e) => setNewBroadcaster({ ...newBroadcaster, logo_color: e.target.value })} className="w-full h-10 rounded-lg cursor-pointer" /></div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowBroadcasterModal(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={createBroadcaster} className="flex-1 btn-primary">Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Broadcaster Modal */}
      {editingBroadcaster && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Edit Broadcaster</h3>
              <button onClick={() => setEditingBroadcaster(null)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label><input type="text" value={editingBroadcaster.name} onChange={(e) => setEditingBroadcaster({ ...editingBroadcaster, name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><textarea value={editingBroadcaster.description} onChange={(e) => setEditingBroadcaster({ ...editingBroadcaster, description: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg" rows={3} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Brand Color</label><input type="color" value={editingBroadcaster.logo_color} onChange={(e) => setEditingBroadcaster({ ...editingBroadcaster, logo_color: e.target.value })} className="w-full h-10 rounded-lg cursor-pointer" /></div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setEditingBroadcaster(null)} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={updateBroadcaster} className="flex-1 btn-primary flex items-center justify-center gap-2"><Save size={18} /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Channel Modal */}
      {showChannelModal && selectedBroadcaster && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add Channel to {selectedBroadcaster.name}</h3>
              <button onClick={() => setShowChannelModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Channel Name *</label><input type="text" value={newChannel.name} onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg" placeholder="e.g., Star Plus HD" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={newChannel.category} onChange={(e) => setNewChannel({ ...newChannel, category: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg">
                    <option>Entertainment</option><option>Movies</option><option>Sports</option><option>News</option><option>Kids</option><option>Music</option><option>Devotional</option><option>Infotainment</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                  <select value={newChannel.language} onChange={(e) => setNewChannel({ ...newChannel, language: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg">
                    <option>Hindi</option><option>English</option><option>Tamil</option><option>Telugu</option><option>Kannada</option><option>Malayalam</option><option>Marathi</option><option>Bengali</option><option>Gujarati</option><option>Punjabi</option><option>Odia</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Quality</label>
                  <select value={newChannel.quality} onChange={(e) => setNewChannel({ ...newChannel, quality: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg">
                    <option>SD</option><option>HD</option><option>FHD</option><option>4K</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label><input type="number" value={newChannel.price} onChange={(e) => setNewChannel({ ...newChannel, price: parseFloat(e.target.value) || 0 })} className="w-full px-4 py-2 border border-gray-200 rounded-lg" /></div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_fta" checked={newChannel.is_fta} onChange={(e) => setNewChannel({ ...newChannel, is_fta: e.target.checked })} className="rounded text-[#D92323]" />
                <label htmlFor="is_fta" className="text-sm text-gray-700">Free-to-Air (FTA) Channel</label>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowChannelModal(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={addChannel} className="flex-1 btn-primary">Add Channel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
